/**
 * What an audit row opens to: every event's raw ids and metadata, with a
 * readiness override's decision spelled out above them.
 *
 * @module components/admin/AuditEventDetail
 */

import React from "react";

import { auditEventLabel } from "@/components/shared/audit-event-labels";
import { KeyValue, MutedText, Text, Time } from "@/components/ui";
import type { AuditEvent } from "@/server/types";

import { auditEventKey, auditMetadataEntries, type AuditRow } from "./audit-trail-rows";

export const OVERRIDE_ACTION = "programme.readiness-overridden";

function metadataString(event: AuditEvent, key: string): string | undefined {
  return typeof event.metadata?.[key] === "string" ? (event.metadata[key] as string) : undefined;
}

function metadataCount(event: AuditEvent, key: string): number | undefined {
  return typeof event.metadata?.[key] === "number" ? (event.metadata[key] as number) : undefined;
}

function OverrideDetail({ event }: { event: AuditEvent }): React.ReactElement {
  const fromState = metadataString(event, "fromState");
  const toState = metadataString(event, "toState");
  const blockerCodes = event.metadata?.blockerCodes;
  const blockerCount = Array.isArray(blockerCodes)
    ? blockerCodes.length
    : metadataCount(event, "blockerCount");
  const reason = metadataString(event, "reason");
  const reference = metadataString(event, "readinessOverrideDecisionId");
  return (
    <KeyValue
      entries={[
        ...(fromState && toState
          ? [{ label: "Transition", value: `${fromState} to ${toState}` }]
          : []),
        ...(blockerCount === undefined
          ? []
          : [{ label: "Bypassed requirements", value: String(blockerCount) }]),
        ...(reason ? [{ label: "Reason", value: reason }] : []),
        ...(reference
          ? [
              {
                label: "Override reference",
                value: (
                  <Text as="span" size="sm" className="font-mono break-all">
                    {reference}
                  </Text>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}

/** The ids behind the resolved names, and the metadata as recorded. */
function RawDetail({ event }: { event: AuditEvent }): React.ReactElement {
  const raw = (value: string): React.ReactElement => (
    <Text as="span" size="sm" className="font-mono break-all">
      {value}
    </Text>
  );
  return (
    <KeyValue
      entries={[
        { label: "Action", value: raw(event.action) },
        { label: "Actor id", value: raw(event.actor) },
        { label: "Target id", value: raw(event.target) },
        ...auditMetadataEntries(event).map(({ label, value }) => ({ label, value: raw(value) })),
      ]}
    />
  );
}

export function AuditEventDetail({ event }: { event: AuditEvent }): React.ReactElement {
  if (event.action !== OVERRIDE_ACTION) {
    return <RawDetail event={event} />;
  }
  return (
    <div className="space-y-3">
      <OverrideDetail event={event} />
      <RawDetail event={event} />
    </div>
  );
}

/** A folded run: each event on its own line, each with its raw detail. */
export function AuditGroupDetail({
  row,
  actorLabel,
}: {
  row: Extract<AuditRow, { kind: "group" }>;
  actorLabel: (event: AuditEvent) => string;
}): React.ReactElement {
  return (
    <ol className="space-y-3">
      {row.events.map((event) => (
        <li key={auditEventKey(event)} className="space-y-1">
          <MutedText>
            {auditEventLabel(event.action)} by {actorLabel(event)},{" "}
            <Time value={event.timestamp} zone />
          </MutedText>
          <RawDetail event={event} />
        </li>
      ))}
    </ol>
  );
}
